import type { FsAnimationEffect } from './fsanimationeffect';
import type { FsNode } from './fsnode';

const KeyframeKeysWithDefaults = new Set(['composite', 'easing', 'offset', 'computedOffset']);
export class FsKeyframeEffect implements FsAnimationEffect {
  private readonly _instance: KeyframeEffect | null = null;
  private _composite: CompositeOperation = 'replace';
  private _iterationComposite: IterationCompositeOperation = 'replace';
  private _keyframes: Keyframe[] | null | undefined;
  private _target: FsNode | null = null;
  private _timing: OptionalEffectTiming | undefined;

  get instance(): KeyframeEffect | null {
    return this._instance ?? null;
  }

  set composite(value: CompositeOperation | undefined) {
    if (value) {
      this._composite = value;
    }
  }

  set iterationComposite(value: IterationCompositeOperation | undefined) {
    if (value) {
      this._iterationComposite = value;
    }
  }

  set target(value: FsNode | null) {
    this._target = value;
  }

  constructor(
    target: FsNode | null,
    keyframes: Keyframe[] | null,
    options?: KeyframeEffectOptions,
  ) {
    if (typeof KeyframeEffect === 'undefined') {
      return;
    }

    // Target cannot be provided to the constructor because the
    // DOM node isn't available until after the flush()
    this._instance = new KeyframeEffect(null, keyframes, options);

    // No need to queue the next target if it matches the default value -- null
    if (target) {
      this._target = target;
    }
  }

  setKeyframes(keyframes: Keyframe[] | null | undefined) {
    const computedKeyframes =
      keyframes && keyframes.length ? new KeyframeEffect(null, keyframes).getKeyframes() : [];
    this._keyframes = computedKeyframes;
  }

  updateTiming(timing?: OptionalEffectTiming) {
    this._timing = timing;
  }

  /**
   * Apply any pending changes to the underlying DOM objects.
   */
  flush() {
    const effect = this._instance;

    if (!effect) {
      return;
    }

    if (this._composite !== effect.composite) {
      effect.composite = this._composite;
    }

    if (this._iterationComposite !== effect.iterationComposite) {
      effect.iterationComposite = this._iterationComposite;
    }

    const nextTarget = (this._target?.dom as Element) ?? null;

    if (nextTarget !== effect.target) {
      effect.target = nextTarget;
    }

    if (this._shouldFlushKeyframes()) {
      effect.setKeyframes(this._keyframes ?? []);
    }

    if (this._shouldFlushTiming()) {
      effect.updateTiming(this._timing);
    }
  }

  /**
   * Returns true if there are any changes in _nextKeyframes
   * that need to be flushed to the effect by comparing properties.
   */
  private _shouldFlushKeyframes(): boolean {
    const computedKeyframes = this.instance?.getKeyframes();

    // Return early if nothing was changed or there's nothing to flush to
    if (typeof computedKeyframes === 'undefined' || typeof this._keyframes === 'undefined') {
      return false;
    }

    // Return early if keyframes are obviously equivalent
    if (computedKeyframes === null && this._keyframes === null) {
      return false;
    }
    if (computedKeyframes.length === 0 && this._keyframes?.length === 0) {
      return false;
    }

    // Return early if keyframes are obviously different
    if (computedKeyframes.length !== this._keyframes?.length) {
      return true;
    }
    if (computedKeyframes && !this._keyframes) {
      return true;
    }

    // Perform an exhaustive check for equality if we couldn't return early
    for (let i = 0; i < computedKeyframes.length; i++) {
      const computed = computedKeyframes[i];
      const next = this._keyframes[i];

      // Skip iterating over properties if we can
      for (const key of KeyframeKeysWithDefaults) {
        if (typeof next[key] !== 'undefined' && computed[key] !== next[key]) {
          return true;
        }
      }

      for (const key in next) {
        if (!keyframeValuesMatch(next[key], computed[key])) {
          return true;
        }
      }

      for (const key in computed) {
        if (KeyframeKeysWithDefaults.has(key)) {
          continue;
        }

        if (!keyframeValuesMatch(computed[key], next[key])) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Returns true if there are changes in _nextTiming that need to be flushed
   */
  private _shouldFlushTiming(): boolean {
    const timing = this.instance?.getTiming();

    // Nothing to flush or flush to
    if (typeof timing === 'undefined' || typeof this._timing === 'undefined') {
      return false;
    }

    // Keys don't match
    for (const key in timing) {
      const name = key as keyof OptionalEffectTiming;
      const next = this._timing[name];
      const current = timing[name];

      if (typeof next !== 'undefined' && next !== current) {
        return true;
      }
    }

    // All keys match, don't flush
    return false;
  }
}

type KeyframeValue = string | number | null | undefined;
function keyframeValuesMatch(a: KeyframeValue, b: KeyframeValue) {
  if (a === b) {
    return true;
  }

  // We can consider null and undefined to be equal
  if (isNullOrUndefined(a) && isNullOrUndefined(b)) {
    return true;
  }

  return false;
}

function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}
